import { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { AlertCircle, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useFormatters } from '../../contexts/PreferencesContext';
import {
  externalApiTokensAPI,
  type ExternalApiToken,
  type ExternalApiTokenWithPlaintext,
} from '../../lib/api/externalApiTokens';
import { CreateTokenModal } from './CreateTokenModal';
import { RevealTokenModal } from './RevealTokenModal';

const POWER_BI_EXAMPLE = `URL:     https://<your-helix-host>/api/external/v1/quality/ncrs/
Headers: Authorization: Bearer helix_ext_<your-token>
         Accept: application/json`;

/**
 * Settings → API Access tab.
 *
 * Lists external API tokens, with create / rotate / revoke actions and a
 * one-time reveal modal for newly-generated plaintext values.
 */
export function APIAccessTab() {
  const { formatDateTime } = useFormatters();

  const [tokens, setTokens] = useState<ExternalApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [revealToken, setRevealToken] = useState<ExternalApiTokenWithPlaintext | null>(null);
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealMode, setRevealMode] = useState<'created' | 'rotated'>('created');

  const [rotateTarget, setRotateTarget] = useState<ExternalApiToken | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ExternalApiToken | null>(null);
  const [actionPending, setActionPending] = useState(false);

  const fetchTokens = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await externalApiTokensAPI.list();
      setTokens(response.data.results);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to load API tokens.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  const handleCreated = (newToken: ExternalApiTokenWithPlaintext) => {
    setRevealToken(newToken);
    setRevealMode('created');
    setRevealOpen(true);
    fetchTokens();
    toast.success('API token created.');
  };

  const handleRotateConfirm = async () => {
    if (!rotateTarget) return;
    setActionPending(true);
    try {
      const response = await externalApiTokensAPI.rotate(rotateTarget.id);
      setRotateTarget(null);
      setRevealToken(response.data);
      setRevealMode('rotated');
      setRevealOpen(true);
      await fetchTokens();
      toast.success('API token rotated.');
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to rotate token.';
      toast.error(message);
    } finally {
      setActionPending(false);
    }
  };

  const handleRevokeConfirm = async () => {
    if (!revokeTarget) return;
    setActionPending(true);
    try {
      await externalApiTokensAPI.revoke(revokeTarget.id);
      setRevokeTarget(null);
      await fetchTokens();
      toast.success('API token revoked.');
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to revoke token.';
      toast.error(message);
    } finally {
      setActionPending(false);
    }
  };

  const renderDate = (value: string | null): string => {
    if (!value) return '—';
    try {
      return formatDateTime(value);
    } catch {
      return value;
    }
  };

  const hasTokens = tokens.length > 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle>API Tokens</CardTitle>
            <CardDescription>
              Manage long-lived tokens used by external integrations such as
              Power BI, BI tools, or partner systems to read your Helix data.
            </CardDescription>
          </div>
          {hasTokens && !loading && (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Token
            </Button>
          )}
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-destructive">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">Could not load tokens</p>
                <p className="text-sm">{error}</p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchTokens}>
                Retry
              </Button>
            </div>
          ) : !hasTokens ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-sm text-muted-foreground max-w-md">
                No API tokens yet. Create one to enable external integrations
                like Power BI.
              </p>
              <Button className="mt-4" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Token
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Label</TableHead>
                  <TableHead>Prefix</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tokens.map((token) => (
                  <TableRow key={token.id}>
                    <TableCell className="font-medium">{token.label}</TableCell>
                    <TableCell>
                      <code className="font-mono text-xs">{token.key_prefix}</code>
                    </TableCell>
                    <TableCell>{renderDate(token.created_at)}</TableCell>
                    <TableCell>{renderDate(token.last_used_at)}</TableCell>
                    <TableCell>
                      {token.expires_at ? renderDate(token.expires_at) : 'Never'}
                    </TableCell>
                    <TableCell>
                      {token.is_active ? (
                        <Badge variant="default">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Revoked</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!token.is_active}
                          onClick={() => setRotateTarget(token)}
                        >
                          <RefreshCw className="h-3.5 w-3.5 mr-1" />
                          Rotate
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!token.is_active}
                          onClick={() => setRevokeTarget(token)}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          Revoke
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Help / Power BI example */}
      <Card>
        <CardHeader>
          <CardTitle>Connecting from Power BI</CardTitle>
          <CardDescription>
            Use the token above as a Bearer credential against the read-only
            external API surface.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <pre className="rounded-md border bg-muted p-3 text-xs overflow-x-auto">
            <code>{POWER_BI_EXAMPLE}</code>
          </pre>
          <p className="text-sm text-muted-foreground">
            Use <code className="font-mono text-xs">?updated_at__gte=2026-01-01</code>
            {' '}to fetch only changed records (incremental refresh).
          </p>
        </CardContent>
      </Card>

      <CreateTokenModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={handleCreated}
      />

      <RevealTokenModal
        open={revealOpen}
        onOpenChange={(next) => {
          setRevealOpen(next);
          if (!next) setRevealToken(null);
        }}
        token={revealToken}
        mode={revealMode}
      />

      {/* Rotate confirmation */}
      <AlertDialog
        open={!!rotateTarget}
        onOpenChange={(next) => {
          if (!next && !actionPending) setRotateTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rotate this token?</AlertDialogTitle>
            <AlertDialogDescription>
              The current token for <strong>{rotateTarget?.label}</strong> will
              stop working immediately. A new plaintext token will be shown
              once — make sure to update any integrations that use it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={actionPending}
              onClick={(e) => {
                e.preventDefault();
                handleRotateConfirm();
              }}
            >
              {actionPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Rotating...
                </>
              ) : (
                'Rotate Token'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke confirmation */}
      <AlertDialog
        open={!!revokeTarget}
        onOpenChange={(next) => {
          if (!next && !actionPending) setRevokeTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this token?</AlertDialogTitle>
            <AlertDialogDescription>
              The token for <strong>{revokeTarget?.label}</strong> will be
              permanently disabled. Any integration using it will lose access
              immediately. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={actionPending}
              onClick={(e) => {
                e.preventDefault();
                handleRevokeConfirm();
              }}
            >
              {actionPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Revoking...
                </>
              ) : (
                'Revoke Token'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
